import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Card, Stack, Typography, Grid, Button, TextField, MenuItem,
  ToggleButtonGroup, ToggleButton, Autocomplete, CircularProgress, Alert,
  IconButton, Chip, Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Assessment as ReportIcon, Print as PrintIcon,
  Description as WordIcon, Close as CloseIcon,
  Search as SearchIcon, Person as PersonIcon,
  ChevronLeft as BackIcon, PrintOutlined as BulkPrintIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import swal from '../utils/swal';
import { reportCardHTML, openPrint, downloadWord, REPORT_CSS } from '../pages/examination/reportCardHtml';
import BulkReportPreview from '../pages/examination/BulkReportPreview';

// Teacher-scope wrapper around the existing report-card builder. Opens
// pinned to a specific (class, term, acc_yer) bucket the class teacher
// owns. Left rail lists the roster; picking a student loads the report
// and previews it live on the right, matching the admin StudentReport
// page but scoped so the class teacher never sees another class.

const EXAM_TYPES = [
  { v: 'terminal', label: 'Terminal' },
  { v: 'mock',     label: 'Mock' },
  { v: 'mid',      label: 'Mid-Term' },
];

export default function ExamReportDialog({ open, onClose, bucket }) {
  const theme = useTheme();
  const cla     = bucket?.cla     || '';
  const term    = bucket?.term    || '';
  const acc_yer = bucket?.acc_yer || '';

  const [examType, setExamType] = useState('terminal');
  const [examNum, setExamNum]   = useState('');
  const [q, setQ]               = useState('');
  const [roster, setRoster]     = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [student, setStudent]   = useState(null);

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Bulk-preview state — when populated, we render a full-page overlay
  // that lists every student report on scroll so the teacher can
  // eyeball the whole class before hitting Print.
  const [bulkList,  setBulkList]  = useState(null);  // array or null

  // Reset everything when the dialog reopens with a new bucket so we
  // don't show a stale preview from another class.
  useEffect(() => {
    if (!open) return;
    setStudent(null); setData(null); setErr(''); setExamNum('');
    setExamType('terminal'); setQ('');
  }, [open, cla, term, acc_yer]);

  // Roster load — scoped to the bucket's class only.
  useEffect(() => {
    if (!open || !cla) { setRoster([]); return; }
    setRosterLoading(true);
    API.get('/examination/scores/roster', { params: { cla } })
      .then(r => setRoster(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRoster([]))
      .finally(() => setRosterLoading(false));
  }, [open, cla]);

  const filteredRoster = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return roster;
    return roster.filter(s =>
      (s.name || '').toLowerCase().includes(needle)
      || (s.ind || '').toLowerCase().includes(needle)
    );
  }, [roster, q]);

  // Auto-load the report as soon as the teacher clicks a student and
  // every required scope field is present. The teacher explicitly picks
  // the exam type / exam number, so no extra button press is needed.
  const canLoad = !!(student?.ind && examType && term && acc_yer
                   && (examType === 'terminal' || examNum));

  const load = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true); setErr(''); setData(null);
    try {
      const r = await API.get(`/examination/reports/student/${encodeURIComponent(student.ind)}`, {
        params: { exam_type: examType, term, acc_yer, exam_num: examNum },
      });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not load report');
    } finally { setLoading(false); }
  }, [canLoad, student, examType, examNum, term, acc_yer]);

  useEffect(() => { load(); /* re-fetch whenever the pick changes */ }, [load]);

  const handlePrint = () => {
    if (!data) return;
    openPrint(reportCardHTML(data), `Report-${data.student?.fullname || ''}`);
  };
  const handleWord = () => {
    if (!data) return;
    const fname = `Report-${(data.student?.fullname || 'student').replace(/\s+/g,'_')}.doc`;
    downloadWord(reportCardHTML(data), fname, fname);
  };

  // Show-all — pulls every student's report for the current bucket
  // and opens an in-app preview overlay the teacher can scroll to
  // eyeball the whole class before deciding to print or download.
  // The former "print immediately" flow is now a button INSIDE the
  // preview, so nothing prints without an explicit confirmation.
  const handleShowAll = async () => {
    if (bulkBusy) return;
    if (examType !== 'terminal' && !examNum) {
      swal.warning('Enter the mock / mid-term number first.');
      return;
    }
    setBulkBusy(true);
    try {
      const r = await API.get('/examination/reports/class', {
        params: { cla, exam_type: examType, term, acc_yer, exam_num: examNum },
      });
      const list = Array.isArray(r.data) ? r.data : [];
      // Show ALL students — including those without scores yet — so
      // the teacher sees the full roster and can decide whether to
      // print now or come back after data-entry. Stub rows fall
      // through the same reportCardHTML path and render as
      // "No subject scores captured for this scope." — clearer than
      // silently hiding them.
      const usable = list.filter(x => !x?.error);
      if (usable.length === 0) {
        swal.warning('No students matched this class for the term / year.');
        return;
      }
      setBulkList(usable);
    } catch (e) {
      swal.error(swal.pickError(e, 'Show all failed'));
    } finally { setBulkBusy(false); }
  };

  const bulkPrintAll = () => {
    if (!bulkList || bulkList.length === 0) return;
    const html = bulkList.map(d => reportCardHTML(d)).join('\n');
    openPrint(html, `Class-${cla}-${term}-${examType}`);
  };
  const bulkPrintOne = (d) => openPrint(reportCardHTML(d), `Report-${d.student?.fullname || ''}`);
  const bulkWordOne  = (d) => {
    const fname = `Report-${(d.student?.fullname || 'student').replace(/\s+/g,'_')}.doc`;
    downloadWord(reportCardHTML(d), fname, fname);
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen
      PaperProps={{ sx: { bgcolor: 'grey.100' } }}>
      {/* Header bar */}
      <Box sx={{
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
        color: '#fff', px: { xs: 1.5, md: 3 }, py: 1.2,
        display: 'flex', alignItems: 'center', gap: 1.4, flexWrap: 'wrap',
      }}>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <BackIcon />
        </IconButton>
        <ReportIcon />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, lineHeight: 1.15, fontSize: { xs: 15, md: 17 } }}>
            Examination Report Cards
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.92, display: 'block', lineHeight: 1.15 }}>
            {cla} · {(term || '').replace('_', ' ')} · {acc_yer}
          </Typography>
        </Box>
        <Chip size="small"
          label={data ? 'Report loaded' : student ? 'Loading…' : 'Pick a student'}
          sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 700 }} />
        <Button variant="contained" color="inherit"
          startIcon={bulkBusy ? <CircularProgress size={14} /> : <BulkPrintIcon />}
          disabled={bulkBusy}
          onClick={handleShowAll}
          sx={{
            bgcolor: 'rgba(255,255,255,0.14)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.55)',
            textTransform: 'none', fontWeight: 700,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
          }}>
          {bulkBusy ? 'Loading…' : 'Show all students'}
        </Button>
        <Button variant="contained" color="inherit" startIcon={<PrintIcon />} disabled={!data}
          onClick={handlePrint}
          sx={{
            bgcolor: 'rgba(255,255,255,0.92)', color: 'primary.main',
            textTransform: 'none', fontWeight: 700,
            '&:hover': { bgcolor: '#fff' },
          }}>
          Print
        </Button>
        <Button variant="outlined" color="inherit" startIcon={<WordIcon />} disabled={!data}
          onClick={handleWord}
          sx={{
            borderColor: 'rgba(255,255,255,0.6)', color: '#fff',
            textTransform: 'none', fontWeight: 700,
            '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
          }}>
          Word
        </Button>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 0 }}>
        {/* Left rail — scope + roster */}
        <Box sx={{
          width: { xs: '100%', md: 320 },
          borderRight: { md: 1 }, borderColor: { md: 'divider' },
          bgcolor: '#fff',
          display: 'flex', flexDirection: 'column',
          maxHeight: { xs: '40vh', md: 'none' },
        }}>
          {/* Exam-type toggle + exam number */}
          <Box sx={{ p: 1.6, borderBottom: 1, borderColor: 'divider' }}>
            <Typography sx={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase',
              letterSpacing: 0.5, color: 'text.secondary', mb: 0.8 }}>
              Exam type
            </Typography>
            <ToggleButtonGroup fullWidth size="small" exclusive value={examType}
              onChange={(_, v) => v && setExamType(v)}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, py: 0.4 } }}>
              {EXAM_TYPES.map(t => (
                <ToggleButton key={t.v} value={t.v}>{t.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
            {examType !== 'terminal' && (
              <TextField fullWidth size="small" sx={{ mt: 1.2 }}
                label={examType === 'mock' ? 'Mock number' : 'Mid-Term number'}
                value={examNum} onChange={e => setExamNum(e.target.value)}
                placeholder="e.g. 1" />
            )}
          </Box>

          {/* Roster picker */}
          <Box sx={{ p: 1.6, borderBottom: 1, borderColor: 'divider' }}>
            <TextField fullWidth size="small" placeholder="Search student…"
              value={q} onChange={e => setQ(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.6, color: 'action.active' }} /> }} />
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', px: 0.6, py: 0.8 }}>
            {rosterLoading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            {!rosterLoading && filteredRoster.length === 0 && (
              <Alert severity="info" sx={{ m: 1 }}>
                {q ? 'No students match.' : 'No students in this class yet.'}
              </Alert>
            )}
            <Stack spacing={0.4}>
              {filteredRoster.map(s => (
                <Box key={s.ind}
                  onClick={() => setStudent(s)}
                  sx={{
                    px: 1.2, py: 0.9, borderRadius: 1.4, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 1,
                    bgcolor: student?.ind === s.ind ? alpha(theme.palette.primary.main, 0.14) : 'transparent',
                    border: '1px solid',
                    borderColor: student?.ind === s.ind ? 'primary.main' : 'transparent',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                  }}>
                  <PersonIcon fontSize="small"
                    sx={{ color: student?.ind === s.ind ? 'primary.main' : 'action.active' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontWeight: student?.ind === s.ind ? 800 : 600,
                      fontSize: 13, lineHeight: 1.2,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {s.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                      {s.ind}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>

        {/* Right — preview */}
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1, md: 2 } }}>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          {!student && !err && (
            <Alert severity="info" icon={<ReportIcon />} sx={{ mb: 2 }}>
              Pick a student on the left to preview their report card.
            </Alert>
          )}
          {loading && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Building report…
              </Typography>
            </Box>
          )}
          {data && !loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ bgcolor: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
                <style>{REPORT_CSS}</style>
                <div dangerouslySetInnerHTML={{ __html: reportCardHTML(data) }} />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Bulk preview overlay — a second full-screen surface layered on
          top of the main dialog. Renders every student's report card
          fragment inline so the teacher can scroll the whole class,
          then decide to print the stack (opens the print window on
          demand), print just one, or download an individual as Word. */}
      {bulkList && (
        <BulkReportPreview
          list={bulkList}
          subtitle={`${cla} · ${(term || '').replace('_', ' ')}`}
          onClose={() => setBulkList(null)}
          onPrintAll={bulkPrintAll}
          onPrintOne={bulkPrintOne}
          onWordOne={bulkWordOne} />
      )}
    </Dialog>
  );
}

